// DailyCashReport.js - Dagskassarapport

import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { getDailyCashReport, getClinicUsers, getReceiptPdf, getSaleReceiptPdf, deletePayment, exportDailyCashReport, emailReceipt } from './calendarApi';
import { UserContext } from '../../UserContext';
import './DailyCashReport.css';

const PAYMENT_METHOD_LABELS = {
  card: { label: 'Kort (Nets)', icon: 'fa-credit-card', color: '#0d6efd' },
  cash: { label: 'Kontant', icon: 'fa-money-bill-wave', color: '#28a745' },
  swish: { label: 'Swish', icon: 'fa-mobile-alt', color: '#6f42c1' },
  frikort: { label: 'Frikort', icon: 'fa-id-card', color: '#6c757d' },
  // Uteblivet räknas inte in i "Total dagskassa" (faktureras separat) - backend styr totalen
  uteblivet: { label: 'Uteblivet', icon: 'fa-user-slash', color: '#dc3545' },
  // Avgiftsfritt (ej fysiskt besök) - alltid 0 kr, utanför totalen, inget kvitto
  avgiftsfri: { label: 'Avgiftsfritt (ej fysiskt besök)', icon: 'fa-phone', color: '#20c997' }
};

const PAYMENT_TYPE_LABELS = {
  appointment: { label: 'Bokning', icon: 'fa-calendar-check', color: '#3b82f6' },
  sale: { label: 'Försäljning', icon: 'fa-shopping-cart', color: '#10b981' }
};

// Intervalltyper
const INTERVAL_TYPES = {
  day: { label: 'Dag', icon: 'fa-calendar-day' },
  week: { label: 'Vecka', icon: 'fa-calendar-week' },
  month: { label: 'Månad', icon: 'fa-calendar-alt' },
  custom: { label: 'Anpassat', icon: 'fa-calendar-range' }
};

// Hjälpfunktioner för datumintervall
const parseDateString = (dateStr) => {
  // Parsa YYYY-MM-DD utan timezone-problem
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Måndag som första dag
  return new Date(d.getFullYear(), d.getMonth(), diff);
};

const getEndOfWeek = (date) => {
  const start = getStartOfWeek(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return end;
};

const getStartOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const getEndOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const formatDateToISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DailyCashReport = () => {
  const { user: currentUser } = useContext(UserContext);
  const [intervalType, setIntervalType] = useState('day');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [clinicUsers, setClinicUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Beräkna start och slutdatum baserat på intervalltyp
  const { effectiveStartDate, effectiveEndDate } = useMemo(() => {
    const baseDate = parseDateString(startDate);
    switch (intervalType) {
      case 'week':
        return {
          effectiveStartDate: formatDateToISO(getStartOfWeek(baseDate)),
          effectiveEndDate: formatDateToISO(getEndOfWeek(baseDate))
        };
      case 'month':
        return {
          effectiveStartDate: formatDateToISO(getStartOfMonth(baseDate)),
          effectiveEndDate: formatDateToISO(getEndOfMonth(baseDate))
        };
      case 'custom':
        return {
          effectiveStartDate: startDate,
          effectiveEndDate: endDate
        };
      case 'day':
      default:
        return {
          effectiveStartDate: startDate,
          effectiveEndDate: startDate
        };
    }
  }, [intervalType, startDate, endDate]);

  // Export handler
  const handleExport = async (format = 'csv') => {
    try {
      setExporting(true);
      await exportDailyCashReport(effectiveStartDate, effectiveEndDate, selectedUserId || null, format);
    } catch (err) {
      console.error('Export error:', err);
      alert('Kunde inte exportera rapport');
    } finally {
      setExporting(false);
    }
  };

  // Ladda anvandare
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await getClinicUsers();
        setClinicUsers(users.filter(u => u.clinic_id === currentUser?.clinic_id));
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };
    if (currentUser) loadUsers();
  }, [currentUser]);

  // Ladda rapport
  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDailyCashReport(effectiveStartDate, effectiveEndDate, selectedUserId || null);
      setReport(data);
    } catch (err) {
      console.error('Error loading report:', err);
      setError('Kunde inte ladda kassarapport');
    } finally {
      setLoading(false);
    }
  }, [effectiveStartDate, effectiveEndDate, selectedUserId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDownloadReceipt = async (payment) => {
    try {
      if (payment.type === 'sale' && payment.sale?.sale_id) {
        await getSaleReceiptPdf(payment.sale.sale_id);
      } else {
        await getReceiptPdf(payment.payment_id);
      }
    } catch (err) {
      alert('Kunde inte hamta kvitto');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Ar du saker pa att du vill makulera denna betalning?')) {
      return;
    }
    try {
      await deletePayment(paymentId);
      loadReport();
    } catch (err) {
      alert('Kunde inte makulera betalning');
    }
  };

  const handleEmailReceipt = async (payment) => {
    try {
      const result = await emailReceipt(payment.payment_id);
      alert(`Kvitto skickat till ${result.email}`);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Kunde inte skicka kvitto';
      alert(errorMsg);
    }
  };

  // Navigera datum baserat på intervalltyp
  const navigatePeriod = (direction) => {
    const date = new Date(startDate);
    switch (intervalType) {
      case 'week':
        date.setDate(date.getDate() + (direction * 7));
        break;
      case 'month':
        date.setMonth(date.getMonth() + direction);
        break;
      case 'day':
      default:
        date.setDate(date.getDate() + direction);
        break;
    }
    setStartDate(formatDateToISO(date));
    if (intervalType === 'custom') {
      // För anpassat intervall, flytta slutdatum lika mycket
      const endDateObj = new Date(endDate);
      endDateObj.setDate(endDateObj.getDate() + direction);
      setEndDate(formatDateToISO(endDateObj));
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateDisplay = (start, end) => {
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };

    if (start === end) {
      return startDateObj.toLocaleDateString('sv-SE', options);
    }

    // Kortare format för intervall
    const shortOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    return `${startDateObj.toLocaleDateString('sv-SE', shortOptions)} - ${endDateObj.toLocaleDateString('sv-SE', shortOptions)}`;
  };

  if (loading && !report) {
    return <div className="dcr-loading">Laddar kassarapport...</div>;
  }

  return (
    <div className="daily-cash-report">
      <div className="dcr-header">
        <h2>Kassarapport</h2>

        {/* Intervallväljare */}
        <div className="dcr-interval-selector">
          {Object.entries(INTERVAL_TYPES).map(([type, info]) => (
            <button
              key={type}
              className={`dcr-interval-btn ${intervalType === type ? 'active' : ''}`}
              onClick={() => setIntervalType(type)}
              title={info.label}
            >
              <i className={`fas ${info.icon}`}></i>
              <span>{info.label}</span>
            </button>
          ))}
        </div>

        {/* Datumväljare */}
        <div className="dcr-controls">
          <div className="dcr-date-nav">
            <button className="nav-btn" onClick={() => navigatePeriod(-1)}>
              <i className="fas fa-chevron-left"></i>
            </button>

            {intervalType === 'custom' ? (
              <div className="dcr-date-range">
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="dcr-date-input"
                />
                <span className="dcr-date-separator">till</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="dcr-date-input"
                  min={startDate}
                />
              </div>
            ) : (
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="dcr-date-input"
              />
            )}

            <button className="nav-btn" onClick={() => navigatePeriod(1)}>
              <i className="fas fa-chevron-right"></i>
            </button>
            <button
              className="nav-btn today-btn"
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setStartDate(today);
                setEndDate(today);
              }}
            >
              Idag
            </button>
          </div>

          {/* Användarfilter */}
          <div className="dcr-filter">
            <label>
              <i className="fas fa-user-md"></i>
              Behandlare:
            </label>
            <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
              <option value="">Alla</option>
              {clinicUsers.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Export-knappar */}
          <div className="dcr-export">
            <button
              className="dcr-export-btn"
              onClick={() => handleExport('csv')}
              disabled={exporting || !report?.payments?.length}
              title="Exportera till CSV"
            >
              <i className="fas fa-file-csv"></i>
              {exporting ? 'Exporterar...' : 'CSV'}
            </button>
            <button
              className="dcr-export-btn dcr-export-fortnox"
              onClick={() => handleExport('fortnox')}
              disabled={exporting || !report?.payments?.length}
              title="Exportera till Fortnox-format"
            >
              <i className="fas fa-file-export"></i>
              {exporting ? 'Exporterar...' : 'Fortnox'}
            </button>
          </div>
        </div>
      </div>

      <div className="dcr-date-display">
        {formatDateDisplay(effectiveStartDate, effectiveEndDate)}
      </div>

      {error && <div className="dcr-error">{error}</div>}

      {report && (
        <>
          {/* Summering */}
          <div className="dcr-summary">
            <div className="dcr-total-card">
              <div className="dcr-total-label">Total dagskassa</div>
              <div className="dcr-total-amount">{report.total.toFixed(2)} kr</div>
              <div className="dcr-total-count">{report.count} betalningar</div>
            </div>

            <div className="dcr-method-summary">
              {Object.entries(PAYMENT_METHOD_LABELS).map(([method, info]) => (
                <div key={method} className="dcr-method-card" style={{ borderTopColor: info.color }}>
                  <div className="dcr-method-icon" style={{ color: info.color }}>
                    <i className={`fas ${info.icon}`}></i>
                  </div>
                  <div className="dcr-method-info">
                    <span className="dcr-method-label">{info.label}</span>
                    <span className="dcr-method-amount">
                      {(report.summary[method] || 0).toFixed(2)} kr
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Friskvårdssammanfattning */}
            {report.friskvard_summary && report.friskvard_summary.count > 0 && (
              <div className="dcr-friskvard-summary">
                <div className="dcr-friskvard-card">
                  <div className="dcr-friskvard-icon">
                    <i className="fas fa-heart"></i>
                  </div>
                  <div className="dcr-friskvard-info">
                    <span className="dcr-friskvard-label">Friskvård</span>
                    <span className="dcr-friskvard-amount">
                      {report.friskvard_summary.total.toFixed(2)} kr
                    </span>
                    <span className="dcr-friskvard-count">
                      {report.friskvard_summary.count} betalningar
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Lista över betalningar */}
          <div className="dcr-payments">
            <h3>Betalningar</h3>
            {report.payments.length === 0 ? (
              <p className="dcr-empty">Inga betalningar registrerade för denna period.</p>
            ) : (
              <div className="dcr-table-container">
                <table className="dcr-table">
                  <thead>
                    <tr>
                      <th>Tid</th>
                      <th>Kvittonr</th>
                      <th>Typ</th>
                      <th>Patient</th>
                      <th>Tjänst</th>
                      <th>Metod</th>
                      <th>Belopp</th>
                      <th>Behandlare</th>
                      <th>Registrerad av</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.payments.map(payment => {
                      const typeInfo = PAYMENT_TYPE_LABELS[payment.type] || PAYMENT_TYPE_LABELS.appointment;
                      const patientName = payment.type === 'sale' && payment.sale
                        ? payment.sale.patient_name
                        : payment.appointment?.patient?.fullname;
                      const serviceName = payment.type === 'sale'
                        ? 'Produktförsäljning'
                        : payment.appointment?.appointment_type?.name;
                      const isFriskvard = payment.is_friskvard || (payment.type === 'sale' && payment.sale?.is_friskvard);

                      return (
                        <tr key={payment.payment_id} className={isFriskvard ? 'dcr-friskvard-row' : ''}>
                          <td className="dcr-time">
                            {formatTime(payment.payment_date)}
                          </td>
                          <td className="dcr-receipt-num">
                            {payment.receipt_number}
                          </td>
                          <td>
                            <span
                              className="dcr-type-badge"
                              style={{ backgroundColor: typeInfo.color }}
                            >
                              <i className={`fas ${typeInfo.icon}`}></i>
                              {typeInfo.label}
                            </span>
                          </td>
                          <td>{patientName || '-'}</td>
                          <td>
                            {serviceName || '-'}
                            {isFriskvard && (
                              <span className="dcr-friskvard-badge" title="Friskvård">
                                <i className="fas fa-heart"></i>
                              </span>
                            )}
                          </td>
                          <td>
                            <span
                              className="dcr-method-badge"
                              style={{ backgroundColor: PAYMENT_METHOD_LABELS[payment.payment_method]?.color }}
                            >
                              <i className={`fas ${PAYMENT_METHOD_LABELS[payment.payment_method]?.icon}`}></i>
                              {PAYMENT_METHOD_LABELS[payment.payment_method]?.label}
                            </span>
                          </td>
                          <td className="dcr-amount">{payment.amount.toFixed(2)} kr</td>
                          <td className="dcr-performer">{payment.treatment_performer || '-'}</td>
                          <td>{payment.registered_by_name}</td>
                          <td className="dcr-actions">
                            {payment.payment_method !== 'avgiftsfri' && (
                              <>
                                <button
                                  className="btn-icon"
                                  onClick={() => handleDownloadReceipt(payment)}
                                  title="Ladda ner kvitto"
                                >
                                  <i className="fas fa-receipt"></i>
                                </button>
                                <button
                                  className="btn-icon btn-icon-email"
                                  onClick={() => handleEmailReceipt(payment)}
                                  title="Skicka kvitto via mail"
                                >
                                  <i className="fas fa-envelope"></i>
                                </button>
                              </>
                            )}
                            <button
                              className="btn-icon btn-icon-danger"
                              onClick={() => handleDeletePayment(payment.payment_id)}
                              title="Makulera betalning"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DailyCashReport;
